'use client';

import { Field } from '../../components/field';
import { Input } from '../../components/input';
import { MetaDefault } from '../../components/typography';
import { Tooltip } from '../../components/tooltip';
import { useTranslations } from 'next-intl';
import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import {
  DEPLOYMENT_DELAY_INPUT_MAX,
  MIN_WAITING_HOURS_FOR_DEPLOYMENT,
} from './deployment-delay-input';

export type DeploymentDelayInputFieldProps = {
  draftDelay: string;
  onDraftDelayChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDraftDelayBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onDelayIntegerKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  showRequiredIndicator?: boolean;
  showHelperText?: boolean;
  inputSize?: 'xs' | 'sm';
  labelFontSize?: 'xs' | 'sm';
  labelColor?: string;
  labelFontWeight?: 'normal' | 'medium';
  minHours?: number;
  disabled?: boolean;
  tooltipTitle?: string;
};

export function DeploymentDelayInputField({
  draftDelay,
  onDraftDelayChange,
  onDraftDelayBlur,
  onDelayIntegerKeyDown,
  showRequiredIndicator = false,
  showHelperText = false,
  inputSize = 'sm',
  labelFontSize,
  labelColor,
  labelFontWeight,
  minHours = MIN_WAITING_HOURS_FOR_DEPLOYMENT,
  disabled,
  tooltipTitle,
}: DeploymentDelayInputFieldProps) {
  const t = useTranslations('designSystem.deploymentDelay');
  const showDisabledTooltip = Boolean(tooltipTitle && disabled);

  return (
    <Field.Root required={showRequiredIndicator}>
      <Field.Label
        fontSize={labelFontSize}
        color={labelColor}
        fontWeight={labelFontWeight}
      >
        {showRequiredIndicator ? (
          <MetaDefault as="span" color="fg.neutral.error">
            *{' '}
          </MetaDefault>
        ) : null}
        {t('label')}
      </Field.Label>
      <Tooltip
        title={tooltipTitle}
        positioning={{
          strategy: 'fixed',
          placement: 'top',
          gutter: 10,
        }}
        disabled={!showDisabledTooltip}
        anchorToPointerOnEnter
      >
        <Input
          placeholder={t('placeholder')}
          value={draftDelay}
          onChange={onDraftDelayChange}
          onBlur={onDraftDelayBlur}
          onKeyDown={onDelayIntegerKeyDown}
          size={inputSize}
          type="number"
          min={minHours}
          max={DEPLOYMENT_DELAY_INPUT_MAX}
          step={1}
          inputMode="numeric"
          disabled={disabled}
        />
      </Tooltip>
      {showHelperText ? (
        <MetaDefault color="fg.neutral.subtle" lineHeight="short">
          {t('alert')}
        </MetaDefault>
      ) : null}
    </Field.Root>
  );
}
